import React from 'react';
import { X, AlertCircle, LogIn, Key, Mail } from 'lucide-react';

const AccountExistsModal = ({ isOpen, onClose, email, onSignIn, onResetPassword, onUseAnotherEmail }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
      <div className="relative max-w-md w-full">
        {/* Glass modal card with portal glow */}
        <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-[0_0_40px_rgba(56,189,248,0.4),0_0_80px_rgba(59,130,246,0.25)] p-8 animate-fade-in">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 rounded-full border border-white/20 bg-white/10 text-white/80 p-2 hover:text-white hover:bg-white/20 hover:border-white/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-200 transition-all duration-200"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <AlertCircle className="w-8 h-8 text-white drop-shadow-[0_0_12px_rgba(255,255,255,0.6)]" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">
              Account Already Exists
            </h2>
            <p className="text-cyan-100/80">
              An account with <span className="font-semibold text-white">{email}</span> already exists.
            </p>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <p className="text-white/90 font-semibold mb-3 text-sm tracking-wide">
              What would you like to do?
            </p>

            {/* Sign In Option */}
            <button
              onClick={onSignIn}
              className="w-full flex items-center justify-center space-x-3 p-4 rounded-2xl border border-white/25 bg-gradient-to-br from-cyan-300/25 via-sky-500/20 to-blue-700/30 text-sky-50 font-semibold tracking-wide backdrop-blur-2xl shadow-[0_20px_60px_rgba(14,165,233,0.35)] transition-all duration-200 hover:scale-[1.01] hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-200"
            >
              <LogIn className="w-5 h-5 drop-shadow-[0_0_8px_rgba(56,189,248,0.4)]" />
              <span>Sign in with this email</span>
            </button>

            {/* Reset Password Option */}
            <button
              onClick={onResetPassword}
              className="w-full flex items-center justify-center space-x-3 p-4 rounded-2xl border border-white/25 bg-white/10 text-white/90 font-semibold tracking-wide hover:bg-white/20 hover:border-white/30 transition-all duration-200 backdrop-blur-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70"
            >
              <Key className="w-5 h-5 text-cyan-100/80 drop-shadow-[0_0_8px_rgba(56,189,248,0.3)]" />
              <span>Reset my password</span>
            </button>

            {/* Use Different Email Option */}
            <button
              onClick={onUseAnotherEmail}
              className="w-full flex items-center justify-center space-x-3 p-4 rounded-2xl border border-white/25 bg-white/10 text-white/90 font-semibold tracking-wide hover:bg-white/20 hover:border-white/30 transition-all duration-200 backdrop-blur-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70"
            >
              <Mail className="w-5 h-5 text-cyan-100/80 drop-shadow-[0_0_8px_rgba(56,189,248,0.3)]" />
              <span>Use a different email</span>
            </button>
          </div>

          {/* Helper text */}
          <p className="text-center text-white/60 text-sm mt-6">
            Need help? Contact{" "}
            <a
              href="mailto:support@scrolled.eu"
              className="text-cyan-100 hover:text-cyan-200 font-semibold transition-colors"
            >
              support@scrolled.eu
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AccountExistsModal;
